/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useContext, useEffect } from 'react';
import { gql } from '@apollo/client';
import { Query } from '@apollo/client/react/components';
import {
    number, array, arrayOf, string, func, bool, shape,
    object,
} from 'prop-types';
import mbpLogger from '@module-logger/mbp';
import { useDispatch } from 'react-redux';
import { GRAPHQL_ENV } from '../../../../../gql';
import GraphqlAddon from './GraphqlAddon';
import { addToCartDataType, addonGroupsType } from '../common/types';
import { CrossMerch } from '../../../CrossMerchContainer/CrossMerchContainer';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

// multiple network calls are needed for the building structure because the error page is a category page but with some special copy from the 404 content page.
const GraphqlAddonContainer = ({
    brand,
    product,
    skuData,
    selectedAddons,
    handleAddonChange,
    product: {
        brandId,
        partNumber,
    },
    categoryId,
    subscription,
    subscriptionDetails,
    presentation_family,
    disableAddToCart,
    isAddToCartEnabled,
    categoryPath,
    categoryName,
    selectedSku,
    productSkus,
    updateWineStatus,
    deliveryData,
    handleMiniCartModalClick,
    mobileAgeVerificationOpen,
    handleMobileAgeVerification,
    addToCartData,
    addonGroups,
    mixMatch,
    handleAddonAdd,
    addonList,
    handleRemoveAddon,
    selectedMixMatchComponent,
    isPdpRedesignEnabled,
    content,
}) => {
    if (presentation_family !== 'food' || !product?.addonGroups?.length) {
        return null;
    }
    const dispatch = useDispatch();
    const { featureFlags = {} } = useContext(CrossMerch);
    const pdpAddonV2 = featureFlags['pdp-addon-v-2'];
    const pdpInlineAddon = featureFlags['is-inline-addons-pdp-enabled'];
    let contentType = 'addon_banner';
    let title =  '';
    if (pdpAddonV2) {
        contentType = 'add_on_banner_v2';
        title = 'Default';
    }

    useEffect(() => {
        if (pdpInlineAddon) {
            dispatch(trackEvent({
                eventCategory: 'Test Impression',
                eventAction: 'Inline Addons',
                eventLabel: partNumber,
                nonInteraction: '1',
            }));
        } else {
            dispatch(trackEvent({
                eventCategory: 'Test Impression',
                eventAction: 'Addons',
                eventLabel: partNumber,
                nonInteraction: '1',
            }));
        }
    }, []);

    const ADD_ON_PAGE_QUERY = gql`
    query PDPpageQuery {
        findContent(brand: "${brand.domain}", environment: "${GRAPHQL_ENV}", contentType: "${contentType}", query: "{title : ${title}}")
        {
          content
        }
       }
    `;
    return (
        <Query query={ADD_ON_PAGE_QUERY}>
            {({ loading, error, data }) => {
                if (loading) {
                    return null; // skeleton component
                }

                if (error) {
                    mbpLogger.logError({
                        appName: process.env.npm_package_name,
                        query: ADD_ON_PAGE_QUERY,
                        component: 'GraphqlAddonContainer.js',
                        message: 'Error loading data from Graphql',
                        env: GRAPHQL_ENV,
                        error,
                    });
                    return null;
                }

                if (!data || data.findContent?.content?.entries.length === 0) {
                    mbpLogger.logWarning({
                        appName: process.env.npm_package_name,
                        query: ADD_ON_PAGE_QUERY,
                        component: 'GraphqlAddonContainer.js',
                        message: 'No data returned for query',
                        env: GRAPHQL_ENV,
                    });
                    return null;
                }

                return (
                    <GraphqlAddon
                        handleMiniCartModalClick={handleMiniCartModalClick}
                        brand={brand}
                        selectedAddons={selectedAddons}
                        pageData={data}
                        product={product}
                        skuData={skuData}
                        handleAddonChange={handleAddonChange}
                        handleRemoveAddon={handleRemoveAddon}
                        handleAddonAdd={handleAddonAdd}
                        brandId={brandId}
                        partNumber={partNumber}
                        categoryId={categoryId}
                        subscription={subscription}
                        subscriptionDetails={subscriptionDetails}
                        presentation_family={presentation_family}
                        disableAddToCart={disableAddToCart}
                        isAddToCartEnabled={isAddToCartEnabled}
                        categoryPath={categoryPath}
                        categoryName={categoryName}
                        selectedSku={selectedSku}
                        productSkus={productSkus}
                        updateWineStatus={updateWineStatus}
                        deliveryData={deliveryData}
                        mobileAgeVerificationOpen={mobileAgeVerificationOpen}
                        handleMobileAgeVerification={handleMobileAgeVerification}
                        addToCartData={addToCartData}
                        addonGroups={addonGroups}
                        mixMatch={mixMatch}
                        addonList={addonList}
                        pdpAddonV2={pdpAddonV2}
                        selectedMixMatchComponent={selectedMixMatchComponent}
                        pdpInlineAddon={pdpInlineAddon}
                        isPdpRedesignEnabled={isPdpRedesignEnabled}
                        content={content}
                    />
                );
            }}
        </Query>
    );
};

GraphqlAddonContainer.propTypes = {
    brand: shape({
        domain: string,
    }).isRequired,
    product: shape({
        id: string.isRequired,
        name: string.isRequired,
        brandId: string.isRequired,
        partNumber: string.isRequired,
    }).isRequired,
    skuData: arrayOf(
        shape({
            id: string.isRequired,
            name: string.isRequired,
            prices: arrayOf(
                shape({
                    type: string.isRequired,
                    value: number.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
    selectedAddons: arrayOf(string),
    handleAddonChange: func,
    categoryId: string.isRequired,
    subscriptionDetails: shape({
        subscriptionType: string,
        subscriptionDuration: string,
        subscriptionFrequency: string,
    }),
    subscription: shape({
        subscriptionKey: array,
        subscriptionIndefinite: string,
        subscriptionType: array,
        implementProductSubscription: bool,
        viewSubscription: bool,
        isTypeDiscovery: bool,
    }),
    presentation_family: string.isRequired,
    disableAddToCart: bool,
    isAddToCartEnabled: bool,
    categoryPath: string.isRequired,
    categoryName: string.isRequired,
    selectedSku: shape({
        id: string.isRequired,
        minAge: string.isRequired,
    }).isRequired,
    productSkus: shape({
        minAge: string.isRequired,
    }).isRequired,
    deliveryData: shape({
        location_type_options: arrayOf(
            shape({
                name: string,
                value: string,
            }),
        ),
        show: bool,
        title: string,
        zip_code_title: string,
        location_type_title: string,
    }).isRequired,
    updateWineStatus: func.isRequired,
    handleMiniCartModalClick: func.isRequired,
    mobileAgeVerificationOpen: bool,
    handleMobileAgeVerification: func,
    addToCartData: addToCartDataType,
    addonGroups: addonGroupsType.isRequired,
    mixMatch: bool,
    handleAddonAdd: func,
    handleRemoveAddon: func,
    addonList: shape({
        list: arrayOf(shape({
            id: string,
            ageVerifyFlag: bool,
            partNumber: string,
            name: string,
        })),
    }),
    selectedMixMatchComponent: arrayOf(shape({
        brandCode: string,
        componentType: string,
        productId: string,
        productCode: string,
    })),
    isPdpRedesignEnabled: bool,
    content: object.isRequired,
};

GraphqlAddonContainer.defaultProps = {
    selectedAddons: [],
    handleAddonChange: () => {},
    handleAddonAdd: () => {},
    isAddToCartEnabled: true,
    subscriptionDetails: {},
    disableAddToCart: false,
    subscription: {},
    mobileAgeVerificationOpen: false,
    handleMobileAgeVerification: () => { },
    addToCartData: {},
    mixMatch: false,
    addonList: {
        list: [],
    },
    handleRemoveAddon: () => {},
    selectedMixMatchComponent: [],
    isPdpRedesignEnabled: false,
};

export default GraphqlAddonContainer;
